import { Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { callFunction } from "../api/client";
import { Spinner } from "./Spinner";

/* A campaign's own words on its Orum list's call notes: a sentence per stage
 * (the angle), the incentive and ordinance line, and a closing printed at the
 * end of every note. Used by the builder, so they are entered when the
 * campaign is made, and by the campaign's page, to change them.
 *
 * The engine writes none of these. Nestor asked for an angle that goes with
 * the stage (2026-09-25), and the copy standard forbids the engine telling a
 * rep what to say, so the words are whoever builds the campaign's. The
 * incentive line is typed rather than computed while the obligation corpus
 * is unfinished (James, 2026-10-05). The stages are Buzze's words, and only
 * those the campaign's filters can return, both from the engine
 * (api.call_angle_stages, lake.stage_word), so a campaign of existing
 * buildings is not asked about Groundbreaking. */
export type CallNotes = { angles: Record<string, string>; incentives: string; closing: string };

export const EMPTY_CALL_NOTES: CallNotes = { angles: {}, incentives: "", closing: "" };

/* The call notes stored on a campaign's shape. */
export function callNotesFrom(shape: unknown): CallNotes {
  const s = (shape ?? {}) as { call_angles?: unknown; call_note_incentives?: unknown; call_note_closing?: unknown };
  const angles: Record<string, string> = {};
  if (s.call_angles && typeof s.call_angles === "object") {
    for (const [k, v] of Object.entries(s.call_angles as Record<string, unknown>)) {
      if (typeof v === "string") angles[k] = v;
    }
  }
  return {
    angles,
    incentives: typeof s.call_note_incentives === "string" ? s.call_note_incentives : "",
    closing: typeof s.call_note_closing === "string" ? s.call_note_closing : "",
  };
}

/* Compared trimmed and with empty angles left out, so typing into a field
   and clearing it again does not read as a change. */
export function sameCallNotes(a: CallNotes, b: CallNotes): boolean {
  const norm = (c: CallNotes) => JSON.stringify([
    Object.entries(c.angles).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v).sort(),
    c.incentives.trim(), c.closing.trim(),
  ]);
  return norm(a) === norm(b);
}

export function CallNotesFields({ token, filters, value, onChange }: {
  token: string; filters: unknown; value: CallNotes; onChange: (v: CallNotes) => void;
}) {
  const stages = useQuery({
    queryKey: ["call-angle-stages", JSON.stringify(filters ?? {})],
    queryFn: () => callFunction<string[] | null>(token, "call_angle_stages", { p_filters: filters ?? {} }),
  });
  if (stages.isError) return <p className="refusal">{(stages.error as Error).message}</p>;
  if (stages.isLoading) return <p className="muted"><Spinner />Loading.</p>;
  return (
    <div className="angles">
      {(stages.data ?? []).map((word) => (
        <Fragment key={word}>
          <label htmlFor={`angle-${word}`}>{word}</label>
          <input id={`angle-${word}`} type="text" maxLength={240}
                 value={value.angles[word] ?? ""}
                 onChange={(e) => onChange({ ...value, angles: { ...value.angles, [word]: e.target.value } })} />
        </Fragment>
      ))}
      <label htmlFor="note-incentives">Incentives and ordinances</label>
      <input id="note-incentives" type="text" maxLength={300} value={value.incentives}
             onChange={(e) => onChange({ ...value, incentives: e.target.value })} />
      <label htmlFor="note-closing">End of every note</label>
      <textarea id="note-closing" rows={7} maxLength={1500} value={value.closing}
                onChange={(e) => onChange({ ...value, closing: e.target.value })} />
    </div>
  );
}
